import { APIRequestState } from 'api';
import { Recipe } from 'common/hooks/useProducer';
import exhaustivnessCheck from 'common/utils/exhaustivnessCheck';
import {
  InternalBankAccountsActions,
  UserWalletsActions,
  ExternalBankAccountsActions,
  WithdrawalActions,
  DepositActions,
  UserClientsActions,
  UserWalletBalancesActions,
  UserWalletsWithBalanceActions,
  UpdateWalletCreditLimitActions,
  UpdateWalletMinBalanceActions,
  UpdateWalletThresholdActions,
  XactAccountActions,
  UserXactAccountsActions,
} from './actions';
import {
  InternalBankAccountsState,
  WalletsState,
  ExternalBankAccountsState,
  DepositState,
  WithdrawalState,
  ClientsState,
  WalletBalancesState,
  WalletsWithBalanceState,
  XactAccountState,
  UserXactAccountsState,
} from './states';

// #region InternalBankAccount
export const internalBankAccountsRecipe: Recipe<
  InternalBankAccountsState,
  InternalBankAccountsActions
> = (draft, action) => {
  switch (action.type) {
    case 'internal_bank_accounts/started':
      draft.pending = true;
      return;

    case 'internal_bank_accounts/finished':
      draft.pending = false;
      draft.accounts = action.accounts;
      draft.count = action.count || 0;
      draft.next = action.next;
      draft.previous = action.previous;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};
// #endregion

// #region Wallet
export const userWalletsRecipe: Recipe<WalletsState, UserWalletsActions> = (
  draft,
  action
) => {
  switch (action.type) {
    case 'wallets/user/started':
      draft.pending = true;
      return;

    case 'wallets/user/finished':
      draft.pending = false;
      draft.wallets = action.wallets;
      draft.count = action.count || 0;
      draft.next = action.next;
      draft.previous = action.previous;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};

export const updateWalletCreditLimitRecipe: Recipe<
  APIRequestState,
  UpdateWalletCreditLimitActions
> = (draft, action) => {
  switch (action.type) {
    case 'wallets/credit/update/started':
      draft.pending = true;
      return;

    case 'wallets/credit/update/finished':
      draft.pending = false;
      draft.success = action.success;
      return;

    default:
      exhaustivnessCheck(action);
  }
};

export const updateWalletThresholdRecipe: Recipe<
  APIRequestState,
  UpdateWalletThresholdActions
> = (draft, action) => {
  switch (action.type) {
    case 'wallets/threshold/update/started':
      draft.pending = true;
      return;

    case 'wallets/threshold/update/finished':
      draft.pending = false;
      draft.success = action.success;
      return;

    default:
      exhaustivnessCheck(action);
  }
};

export const updateWalletMinBalanceRecipe: Recipe<
  APIRequestState,
  UpdateWalletMinBalanceActions
> = (draft, action) => {
  switch (action.type) {
    case 'wallets/min_balance/update/started':
      draft.pending = true;
      return;

    case 'wallets/min_balance/update/finished':
      draft.pending = false;
      draft.success = action.success;
      return;

    default:
      exhaustivnessCheck(action);
  }
};

export const userWalletBalancesRecipe: Recipe<
  WalletBalancesState,
  UserWalletBalancesActions
> = (draft, action) => {
  switch (action.type) {
    case 'wallets/balances/user/started':
      draft.pending = true;
      return;

    case 'wallets/balances/user/finished':
      draft.pending = false;
      draft.balances = action.balances;
      draft.count = action.count || 0;
      draft.next = action.next;
      draft.previous = action.previous;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};

export const userWalletsWithBalanceRecipe: Recipe<
  WalletsWithBalanceState,
  UserWalletsWithBalanceActions
> = (draft, action) => {
  switch (action.type) {
    case 'wallets_with_balance/user/started':
      draft.pending = true;
      return;

    case 'wallets_with_balance/user/finished':
      draft.pending = false;
      draft.wallets = action.wallets;
      draft.count = action.count || 0;
      draft.next = action.next;
      draft.previous = action.previous;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};
// #endregion

// #region Client
export const userClientsRecipe: Recipe<ClientsState, UserClientsActions> = (
  draft,
  action
) => {
  switch (action.type) {
    case 'clients/user/started':
      draft.pending = true;
      return;

    case 'clients/user/finished':
      draft.pending = false;
      draft.clients = action.clients;
      draft.count = action.count || 0;
      draft.next = action.next;
      draft.previous = action.previous;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};
// #endregion

// #region ExternalBankAccount
export const externalBankAccountsRecipe: Recipe<
  ExternalBankAccountsState,
  ExternalBankAccountsActions
> = (draft, action) => {
  switch (action.type) {
    case 'external_bank_accounts/started':
      draft.pending = true;
      return;

    case 'external_bank_accounts/finished':
      draft.pending = false;
      draft.accounts = action.accounts;
      draft.count = action.count || 0;
      draft.next = action.next;
      draft.previous = action.previous;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};
// #endregion

// #region Deposit
export const depositRecipe: Recipe<DepositState, DepositActions> = (
  draft,
  action
) => {
  switch (action.type) {
    case 'deposit/started':
      draft.pending = true;
      return;

    case 'deposit/finished':
      draft.pending = false;
      draft.deposit = action.deposit;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};
// #endregion

// #region Withdrawal
export const withdrawalRecipe: Recipe<WithdrawalState, WithdrawalActions> = (
  draft,
  action
) => {
  switch (action.type) {
    case 'withdrawal/started':
      draft.pending = true;
      return;

    case 'withdrawal/finished':
      draft.pending = false;
      draft.withdrawal = action.withdrawal;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};
// #endregion

// #region XactAccount
export const xactAccountRecipe: Recipe<XactAccountState, XactAccountActions> = (
  draft,
  action
) => {
  switch (action.type) {
    case 'xact/account/started':
      draft.pending = true;
      return;

    case 'xact/account/finished':
      draft.pending = false;
      draft.account = action.account;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};

export const userXactAccountsRecipe: Recipe<
  UserXactAccountsState,
  UserXactAccountsActions
> = (draft, action) => {
  switch (action.type) {
    case 'xact/user/accounts/started':
      draft.pending = true;
      return;

    case 'xact/user/accounts/finished':
      draft.pending = false;
      draft.accounts = action.accounts;
      draft.count = action.count || 0;
      draft.next = action.next;
      draft.previous = action.previous;
      draft.error = action.error;
      return;

    default:
      exhaustivnessCheck(action);
  }
};
// #endregion
